// react
import { ChangeEvent, ReactNode, useContext, useState } from 'react';

// types
import { Country, FlagCode } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Grid, InputLabel, SelectChangeEvent, Typography } from '@mui/material';

// fields
import { Input, InputProps } from '../Input/Input';
import { Select, SelectProps } from '../Select/Select';

// ui
import { Flag } from '../../ui/Flag/Flag';

// props
export interface InputPhoneValue {
	country: string;
	phone: string;
}

export interface InputPhoneProps {
	InputProps?: InputProps;
	label?: string;
	SelectProps?: SelectProps & {
		valueType?: 'CODE' | 'ID';
	};
	variant?: 'merged' | 'separated';
}

export const InputPhone = ({
	InputProps,
	label,
	SelectProps,
	variant = 'merged',
	...props
}: InputPhoneProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);

	// defaults
	const { valueType, ...selectProps } = SelectProps || {};
	const defaultValues = {
		country: String(SelectProps?.defaultValue || SelectProps?.value || ''),
		phone: String(InputProps?.defaultValue || InputProps?.value || ''),
	};

	// set project country as default country
	if (!defaultValues.country && project?.country) {
		defaultValues.country = String(
			valueType === 'CODE' ? project.country.code : project.country.id
		);
	}

	// state
	const [countries, setCountries] = useState<Country[]>([]);
	const [values, setValues] = useState(defaultValues);

	// fetch countries
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/countries`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCountries(res.data);

				if (!values.country) {
					setValues((prev) => ({
						...prev,
						country: String(res.data[0].id),
					}));
				}
			}
		},
	});

	// options
	const optionsCountries = countries.map((country) => {
		return {
			icon: <Flag code={country.code as FlagCode} />,
			label: (
				<Grid container spacing={1}>
					<Grid item>
						<Typography>{country.name}</Typography>
					</Grid>
					{country.phone && (
						<Grid item>
							<Typography color="brand.grey.500">+{country.phone}</Typography>
						</Grid>
					)}
				</Grid>
			),
			phone: country.phone,
			value: String(valueType === 'CODE' ? country.code : country.id),
		};
	});

	const handleChangeCountry = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const { value } = e.target as HTMLInputElement;

		if (selectProps?.onChange) {
			selectProps.onChange(e, child);
		}

		setValues((prev) => ({
			...prev,
			country: value,
		}));
	};

	const handleChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target as HTMLInputElement;

		if (InputProps?.onChange) {
			InputProps.onChange(e);
		}

		setValues((prev) => ({
			...prev,
			phone: value,
		}));
	};

	return (
		<Grid container>
			{label && (
				<Grid item xs={12}>
					<InputLabel disabled={InputProps?.disabled} required={InputProps?.required}>
						{label}
					</InputLabel>
				</Grid>
			)}
			<Grid item xs={12}>
				<Grid container spacing={variant === 'separated' ? 2 : 0}>
					<Grid item xs="auto">
						<Select
							hasNoneOption={false}
							id="country"
							name="country"
							onChange={handleChangeCountry}
							options={optionsCountries}
							renderValue={(value) => {
								const option = optionsCountries.find((option) => option.value === value);
								if (option) {
									return (
										<Grid container spacing={1}>
											<Grid item>{option.icon || t('na')}</Grid>
											{option.phone && <Grid item>+{option.phone}</Grid>}
										</Grid>
									);
								}
								return value;
							}}
							sx={(theme) => {
								if (variant === 'merged') {
									return {
										borderBottomRightRadius: `0 !important`,
										borderTopRightRadius: `0 !important`,
										'&.MuiInputBase-root:hover, &.Mui-focused': {
											'& fieldset': {
												borderColor: `${theme.palette.divider} !important`,
											},
										},
										'& fieldset': {
											borderRight: 'none !important',
										},
									};
								}
								return {};
							}}
							value={values.country}
							{...selectProps}
						/>
					</Grid>
					<Grid item xs={true}>
						<Input
							id="phone"
							name="phone"
							{...props}
							InputProps={{
								sx: (theme) => {
									if (variant === 'merged') {
										return {
											borderBottomLeftRadius: `0 !important`,
											borderTopLeftRadius: `0 !important`,
											'& input': {
												paddingLeft: 0,
											},
											'&.MuiInputBase-root:hover, &.Mui-focused': {
												'& fieldset': {
													borderColor: `${theme.palette.divider} !important`,
												},
											},
											'& fieldset': {
												borderLeft: 'none !important',
											},
										};
									}

									return {};
								},
							}}
							onChange={handleChangePhone}
							type="tel"
							value={values.phone}
							{...InputProps}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
